import React from 'react';
import { extend } from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import { Card } from '@one-thd/sui-atomic-components';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { CreateAList } from '@thd-olt-component-react/list-forms';
import classNames from 'classnames';
import ListsContainer from './ListsContainer';
import { dataModel } from '../dataModel';
import ListsSkeleton from './ListsSkeleton';
import ListsError from './ListsError';
import ListsNullView from './ListsNullView/ListsNullView';
import { useLists } from '../useLists';

const { bool, number, string } = proptypes;

const Lists = ({
  maxMobileListSize, iconSize, componentId, isDashboardSnapshot, hideViewAllSuffix, fillHeight
}) => {
  const {
    lists, loading, error, refetchLists
  } = useLists({ page: 1 });

  const impressionData = {
    name: 'UserLists',
    component: 'UserLists',
    type: 'content',
    position: '',
    id: componentId || ''
  };

  if (error) {
    return (
      <ListsError refetchLists={refetchLists} isDashboardSnapshot={isDashboardSnapshot} />
    );
  }
  if (loading) {
    return (
      <ListsSkeleton />
    );
  }

  if (!Array.isArray(lists) || lists.length === 0) {
    return (
      <ImpressionProvider data={impressionData}>
        <ListsNullView componentId={componentId} isDashboardSnapshot={isDashboardSnapshot} />
      </ImpressionProvider>
    );
  }

  return (
    <ImpressionProvider data={impressionData}>
      <Card
        data-component="Lists"
        data-testid="lists"
        className={classNames('sui-@container sui-h-full', { 'sui-rounded-md': isDashboardSnapshot })}
        // Dashboard Snapshot already supplies a single 16px inset around the active tab's
        // panel (split across ProAlertsAndUpdates' and SnapshotTabView's own wrappers, never
        // doubled between them). This Card's own sui-p-4 is correct for standalone usage
        // (Account Dashboard, etc.), where it's the only inset — but inside the snapshot it
        // stacked a second, redundant 16px on all four sides on top of the shared one. Your
        // Latest Orders has no Card of its own here and never had this extra layer, which is
        // why it was the one tab actually matching spec (flagged by both QE and UX).
        CardContentProps={{
          className: classNames(
            'sui-flex sui-gap-4 sui-flex-col sui-h-full',
            { 'sui-p-4': !isDashboardSnapshot }
          ),
        }}
        PaperProps={isDashboardSnapshot ? { variant: 'shadow', shadow: 'none' } : undefined}
      >
        <ListsContainer
          lists={lists}
          iconSize={iconSize}
          maxMobileListSize={maxMobileListSize}
          componentId={componentId}
          isDashboardSnapshot={isDashboardSnapshot}
          hideViewAllSuffix={hideViewAllSuffix}
          fillHeight={fillHeight}
        />
      </Card>
    </ImpressionProvider>
  );
};

Lists.propTypes = {
  maxMobileListSize: number,
  iconSize: string,
  componentId: string,
  /**
   * Set true when rendering inside Dashboard Snapshot. Suppresses this component's own
   * "Lists (n)" title and Create New action, and drops the card border, since the
   * snapshot's tabs and panel supply those. View All still renders, moved below the lists
   * and labeled with the list count.
   */
  isDashboardSnapshot: bool,
  /**
   * When true, the View All CTA reads "View All" instead of "View All Lists" at both
   * breakpoints. The accessible name stays "View All Lists" via aria-label either way.
   */
  hideViewAllSuffix: bool,
  /**
   * For consumers that set the card's height from outside (Dashboard Snapshot matches the card
   * beside it): the lists take whatever height is left after View All. On desktop the carousel
   * tiles stretch to fill it; on mobile the compact list keeps its height and View All sits at
   * the bottom. In Dashboard Snapshot it also replaces the tiles' 29rem floor. Off by default.
   */
  fillHeight: bool,
};
Lists.defaultProps = {
  maxMobileListSize: 4,
  iconSize: 'small',
  componentId: undefined,
  isDashboardSnapshot: false,
  hideViewAllSuffix: false,
  fillHeight: false,
};
Lists.displayName = 'Lists';

Lists.dataModel = extend(dataModel, CreateAList);

export { Lists };
